/**
 * Command-Katalog fuer die Command-Palette.
 *
 * Drei Sektionen:
 *  - "Seiten"    — Navigation (aus Sidebar NAV_GROUPS abgeleitet)
 *  - "Erstellen" — Entity-Creation-Shortcuts
 *  - "Aktionen"  — Kontextabhaengige Actions (globale + seitenspezifische)
 *
 * Alle Routes/Modules werden unmodifiziert aus der Sidebar uebernommen
 * (keine Duplizierung), damit nur eine Source-of-Truth existiert.
 *
 * Felder pro Command:
 *   id        — eindeutiger stabiler Key (MRU-Persistenz)
 *   section   — 'Seiten' | 'Erstellen' | 'Aktionen'
 *   title     — Label in der Liste
 *   subtitle  — Optionaler Hinweis / Kurzbeschreibung
 *   keywords  — String fuer Fuzzy-Match (zusaetzliche Synonyme)
 *   icon      — Name aus lucide-react (String)
 *   path      — Ziel-Route (React-Router), optional bei run()
 *   query     — URL-Query-Fragment (z.B. "new=true") — wird an path gehaengt
 *   module    — Modul-Key zur Sichtbarkeits-Pruefung (optional)
 *   shortcut  — Hinweis-Text rechts ("⌘K", "g k", "Enter")
 *   chord     — Einzelbuchstabe fuer "g→letter" (optional)
 *   run       — Zusaetzlicher Handler (z.B. ?action=... Query-Param)
 *   contexts  — Pfad-Prefixe bei denen dieser Command hoeher gewichtet wird
 */

// ── Sidebar importieren (Single-Source-of-Truth) ─────────────────
import { NAV_GROUPS } from './navSource';

// ── Seiten (abgeleitet aus NAV_GROUPS) ───────────────────────────
function flattenNavGroups(groups) {
  const out = [];
  for (const g of groups) {
    if (g.comingSoon) continue;
    if (g.collapsible && Array.isArray(g.items)) {
      for (const child of g.items) {
        if (child.comingSoon) continue;
        if (!child.path) continue;
        out.push({
          id: `nav:${g.key}:${child.key}`,
          section: 'Seiten',
          title: `${g.label} · ${child.label}`,
          subtitle: 'Seite oeffnen',
          keywords: `${g.label} ${child.label} seite navigation`,
          icon: child.icon || g.icon || 'FileText',
          path: child.path,
          module: child.module,
        });
      }
      continue;
    }
    if (!g.path) continue;
    out.push({
      id: `nav:${g.key}`,
      section: 'Seiten',
      title: g.label,
      subtitle: 'Seite oeffnen',
      keywords: `${g.label} seite navigation`,
      icon: g.icon || 'FileText',
      path: g.path,
      module: g.module,
    });
  }
  return out;
}

// ── Statische Seiten-Extras (nicht in Sidebar) ───────────────────
const EXTRA_PAGES = [
  {
    id: 'nav:settings',
    section: 'Seiten',
    title: 'Einstellungen',
    subtitle: 'Konto, Module, Branding',
    keywords: 'settings einstellungen konto account module preferences',
    icon: 'Settings',
    path: '/settings',
  },
  {
    id: 'nav:settings-modules',
    section: 'Seiten',
    title: 'Einstellungen · Module',
    subtitle: 'Module aktivieren/deaktivieren',
    keywords: 'module modules preset aktivieren deaktivieren plan',
    icon: 'Settings',
    path: '/settings?tab=modules',
  },
  {
    id: 'nav:system',
    section: 'Seiten',
    title: 'System · Audit-Log',
    subtitle: 'Admin — DSGVO Audit-Log + Modul-Events',
    keywords: 'system audit dsgvo gdpr log events admin',
    icon: 'Shield',
    path: '/system',
  },
];

// ── "Erstellen" — Entity-Creation Shortcuts ──────────────────────
const CREATE_COMMANDS = [
  {
    id: 'create:blog-post',
    section: 'Erstellen',
    title: 'Neuer Blog-Post',
    subtitle: 'KI-Schreiben im Blog-Tab starten',
    keywords: 'blog post artikel beitrag neu create schreiben ki',
    icon: 'BookOpen',
    path: '/content',
    query: 'tab=blog&new=true',
    module: 'blog',
  },
  {
    id: 'create:landing-page',
    section: 'Erstellen',
    title: 'Neue Landing-Page',
    subtitle: 'Content-Wizard fuer Landing-Page',
    keywords: 'landing page seite wizard kampagne ki create',
    icon: 'Target',
    path: '/content',
    query: 'tab=landing&wizard=true',
    module: 'landing_pages',
  },
  {
    id: 'create:page',
    section: 'Erstellen',
    title: 'Neue Seite',
    subtitle: 'Statische Seite hinzufuegen',
    keywords: 'seite page create neu statisch',
    icon: 'FileText',
    path: '/content',
    query: 'tab=seiten&new=true',
    module: 'website',
  },
  {
    id: 'create:keyword',
    section: 'Erstellen',
    title: 'Neuer Keyword',
    subtitle: 'Keyword in den Pool aufnehmen',
    keywords: 'keyword pool hinzufuegen seo suchbegriff neu',
    icon: 'Key',
    path: '/keywords',
    query: 'new=true',
    module: 'keywords',
  },
  {
    id: 'create:keyword-research',
    section: 'Erstellen',
    title: 'Neue Keyword-Recherche',
    subtitle: 'Freeform-Recherche mit Google Suggest',
    keywords: 'keyword research freeform recherche suggest seo',
    icon: 'Search',
    path: '/keywords',
    query: 'research=true',
    module: 'keywords',
  },
  {
    id: 'create:newsletter',
    section: 'Erstellen',
    title: 'Neuer Newsletter',
    subtitle: 'Kampagne in Compose-View',
    keywords: 'newsletter kampagne email compose brevo create neu',
    icon: 'Mail',
    path: '/newsletter',
    query: 'compose=true',
    module: 'newsletter',
  },
  {
    id: 'create:meta-campaign',
    section: 'Erstellen',
    title: 'Neue Meta-Kampagne',
    subtitle: 'Meta-Ads Wizard starten',
    keywords: 'meta ads facebook instagram kampagne campaign wizard create neu',
    icon: 'Megaphone',
    path: '/meta-ads',
    query: 'wizard=true',
    module: 'ads_meta',
  },
  {
    id: 'create:audience',
    section: 'Erstellen',
    title: 'Neue Audience',
    subtitle: 'Meta-Ads Zielgruppe anlegen',
    keywords: 'audience zielgruppe custom lookalike meta ads create',
    icon: 'Users',
    path: '/meta-ads/audiences',
    query: 'new=true',
    module: 'ads_meta',
  },
  {
    id: 'create:lead-form',
    section: 'Erstellen',
    title: 'Neues Lead-Formular',
    subtitle: 'Lead-Form fuer Meta-Ads',
    keywords: 'lead form formular meta ads gen create',
    icon: 'ClipboardList',
    path: '/meta-ads/leadforms',
    query: 'new=true',
    module: 'ads_meta',
  },
  {
    id: 'create:automation-rule',
    section: 'Erstellen',
    title: 'Neue Automation-Regel',
    subtitle: 'Meta-Ads Rule anlegen',
    keywords: 'automation rule regel meta ads scaling if-then create',
    icon: 'Zap',
    path: '/meta-ads/rules',
    query: 'new=true',
    module: 'ads_meta',
  },
  {
    id: 'create:social-post',
    section: 'Erstellen',
    title: 'Neuer Social-Post',
    subtitle: 'Social-Media Beitrag planen',
    keywords: 'social media post linkedin instagram facebook gbp beitrag create',
    icon: 'Share2',
    path: '/social-media',
    query: 'compose=true',
    module: 'social_media',
  },
  {
    id: 'create:content-package',
    section: 'Erstellen',
    title: 'Neues Content-Paket',
    subtitle: 'Themencluster-Paket starten',
    keywords: 'content package paket cluster topic authority pillar',
    icon: 'Calendar',
    path: '/content-strategy',
    query: 'new=true',
    module: 'content_strategy',
  },
  {
    id: 'create:listing',
    section: 'Erstellen',
    title: 'Neues Listing',
    subtitle: 'Standort-Listing hinzufuegen',
    keywords: 'listing google gbp standort location eintrag create',
    icon: 'MapPin',
    path: '/listings',
    query: 'new=true',
    module: 'listings',
  },
];

// ── "Aktionen" — Kontextabhaengige Actions ───────────────────────
const ACTION_COMMANDS = [
  {
    id: 'action:seo-health',
    section: 'Aktionen',
    title: 'SEO Health Check',
    subtitle: 'SEO-Analyse ueber alle Seiten',
    keywords: 'seo health check analyse score audit run',
    icon: 'Search',
    path: '/seo',
    query: 'action=analyze',
    module: 'seo',
    contexts: ['/seo', '/content', '/keywords'],
  },
  {
    id: 'action:ads-radar',
    section: 'Aktionen',
    title: 'Ads Radar scannen',
    subtitle: 'Meta-Ads Radar Full-Scan',
    keywords: 'ads radar meta facebook scan anomalien check',
    icon: 'Radar',
    path: '/meta-ads/radar',
    query: 'action=scan-all',
    module: 'ads_meta',
    contexts: ['/meta-ads'],
  },
  {
    id: 'action:keyword-research',
    section: 'Aktionen',
    title: 'Keyword-Research starten',
    subtitle: 'Google Suggest Freeform-Flow',
    keywords: 'keyword research freeform google suggest seo start',
    icon: 'Key',
    path: '/keywords',
    query: 'action=research',
    module: 'keywords',
    contexts: ['/keywords', '/content', '/seo'],
  },
  {
    id: 'action:ai-content',
    section: 'Aktionen',
    title: 'AI-Content generieren',
    subtitle: 'Claude Opus — Content-Wizard',
    keywords: 'ai ki content generieren claude opus wizard generate',
    icon: 'Sparkles',
    path: '/content',
    query: 'action=ai-generate',
    module: 'website',
    contexts: ['/content'],
  },
  {
    id: 'action:reply-contact',
    section: 'Aktionen',
    title: 'Kontaktanfrage beantworten',
    subtitle: 'Reply-Flow mit KI-Vorschlag',
    keywords: 'kontakt anfrage antworten reply crm lead beantworten',
    icon: 'MessageSquare',
    path: '/contact-submissions',
    query: 'action=reply',
    module: 'crm',
    contexts: ['/contact-submissions'],
  },
  {
    id: 'action:gsc-sync',
    section: 'Aktionen',
    title: 'GSC-Sync starten',
    subtitle: 'Google Search Console neu syncen',
    keywords: 'gsc google search console sync aktualisieren keywords',
    icon: 'TrendingUp',
    path: '/keywords',
    query: 'action=gsc-sync',
    module: 'keywords',
    contexts: ['/keywords', '/seo'],
  },
  {
    id: 'action:ads-sync',
    section: 'Aktionen',
    title: 'Google Ads syncen',
    subtitle: 'Kampagnen-Daten aktualisieren',
    keywords: 'google ads sync aktualisieren kampagnen performance',
    icon: 'DollarSign',
    path: '/ads',
    query: 'action=sync',
    module: 'ads_google',
    contexts: ['/ads'],
  },
  {
    id: 'action:backup-create',
    section: 'Aktionen',
    title: 'Backup erstellen',
    subtitle: 'Vollstaendiges Website-Backup',
    keywords: 'backup zip website daten sicherung erstellen snapshot',
    icon: 'Shield',
    path: '/system',
    query: 'action=backup',
    contexts: ['/system', '/settings'],
  },
  {
    id: 'action:dna-update',
    section: 'Aktionen',
    title: 'DNA aktualisieren',
    subtitle: 'Unternehmens-DNA pflegen',
    keywords: 'dna unternehmen firma aktualisieren interview update',
    icon: 'Brain',
    path: '/dna',
    query: 'action=update',
    module: 'dna',
  },
  {
    id: 'action:theme-toggle',
    section: 'Aktionen',
    title: 'Theme umschalten (Dark/Light)',
    subtitle: 'Farbschema wechseln',
    keywords: 'theme dark light mode umschalten farbschema',
    icon: 'Palette',
    run: ({ uiStore }) => {
      if (uiStore?.toggleTheme) uiStore.toggleTheme();
    },
  },
];

// ── "g→letter" Chord-Map (Leader-Key) ────────────────────────────
// Mapping statisch, damit es unabhaengig von der Fuzzy-Search funktioniert.
export const CHORD_MAP = {
  d: { path: '/', label: 'Dashboard' },
  k: { path: '/keywords', label: 'Keywords', module: 'keywords' },
  s: { path: '/seo', label: 'SEO', module: 'seo' },
  b: { path: '/content?tab=blog', label: 'Blog', module: 'blog' },
  a: { path: '/analytics', label: 'Analytics', module: 'analytics' },
  i: { path: '/contact-submissions', label: 'Anfragen / Inbox', module: 'crm' },
  c: { path: '/content', label: 'Content' },
  n: { path: '/newsletter', label: 'Newsletter', module: 'newsletter' },
};

// ── Build-Funktion ───────────────────────────────────────────────
/**
 * Erzeugt das vollstaendige, gefilterte Command-Set.
 * @param {object} opts
 * @param {(module:string)=>boolean} opts.isEnabled — Modul-Check
 * @param {string} opts.pathname — aktueller Router-Pfad
 */
export function buildCommands({ isEnabled = () => true, pathname = '/' } = {}) {
  const all = [
    ...flattenNavGroups(NAV_GROUPS),
    ...EXTRA_PAGES,
    ...CREATE_COMMANDS,
    ...ACTION_COMMANDS,
  ];

  const filtered = all.filter((cmd) => {
    if (!cmd.module) return true;
    return isEnabled(cmd.module);
  });

  // Kontext-Boost: wenn wir auf einer Seite sind, die einen Command direkt
  // betrifft, bekommt er einen kleinen "contextBoost", den die Fuse-Stage nicht
  // kennt — wir nutzen ihn fuer die Gruppierung/Sortierung innerhalb einer Sektion.
  return filtered.map((cmd) => {
    let boost = 0;
    if (Array.isArray(cmd.contexts)) {
      for (const prefix of cmd.contexts) {
        if (pathname.startsWith(prefix)) {
          boost = 1;
          break;
        }
      }
    }
    return { ...cmd, _contextBoost: boost };
  });
}

/**
 * Helper: Command-Ziel-URL zusammenbauen (path + query).
 */
export function buildCommandUrl(cmd) {
  if (!cmd || !cmd.path) return null;
  if (!cmd.query) return cmd.path;
  const sep = cmd.path.includes('?') ? '&' : '?';
  return `${cmd.path}${sep}${cmd.query}`;
}
